import { observer } from "mobx-react-lite";
import { Container, Grid, Header, Table } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useStore } from "../../../app/stores/store";
import PageHeader from "../../../app/layout/PageHeader";
import { formatCurrency } from "../../../app/common/util/functions";
import React from "react";
import { AccountsReceivableAgingReportItemDto } from "../../../app/models/accountsReceivable";

interface Props {
  startDate: Date;
  endDate: Date;
}

export default observer(function AgingPrint({ startDate, endDate }: Props) {
  const { t } = useTranslation(["common", "reports"]);

  const { accountsReceivableAgingReportStore } = useStore();
  const {
    getAccountsReceivableAgingReport,
    calculateOneToThirtyBalanceDue,
    calculateThirtyToSixtyBalanceDue,
    calculateSixtyToNinetyBalanceDue,
    calculateNinetyPlusBalanceDue,
    calculateTotalBalanceDue,
  } = accountsReceivableAgingReportStore;

  return (
    <>
      <Container className="page">
        <Grid stackable columns={2} className="headerPrint">
          <Grid.Row>
            <Grid.Column>
              <PageHeader
                header={`${t("report", { ns: "reports" })}: ${t("agingReport", {
                  ns: "accountsReceivable",
                })}`}
                type="h2"
                divider={false}
                getAlerts={true}
                addTitle={true}
              />
            </Grid.Column>
            <Grid.Column>
              <Header
                content={`${format(startDate, "PP")} - ${format(
                  endDate,
                  "PP"
                )}`}
                as="h2"
                textAlign="right"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Table striped unstackable className="dnxTable">
          <Table.Header>
            <Table.HeaderCell>
              {t("customername", { ns: "common" })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("labels.invoice_date", { ns: "inventory" })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("ticketnum", { ns: "common" })}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("daysOutstanding", { ns: "accountsReceivable" })}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              {t("payReceived", { ns: "accountsReceivable" })}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              1 - 30 {t("daysOutstanding", { ns: "accountsReceivable" })}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              31 - 60 {t("daysOutstanding", { ns: "accountsReceivable" })}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              61 - 90 {t("daysOutstanding", { ns: "accountsReceivable" })}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              91+ {t("daysOutstanding", { ns: "accountsReceivable" })}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              {t("balanceDue", { ns: "accountsReceivable" })}
            </Table.HeaderCell>
          </Table.Header>
          <Table.Body>
            {getAccountsReceivableAgingReport && getAccountsReceivableAgingReport.length 
            && getAccountsReceivableAgingReport.map((ag: AccountsReceivableAgingReportItemDto, idx: number) => (
              <Table.Row key={ag.accountsReceivableId}>
                <Table.Cell
                  className="hoveredLink"
                  onClick={() => window.open(`/customer/${ag.customerId}`, "_blank")}
                  data-label={`${t("customername", { ns: "common" })}:`}
                >
                  {ag.companyName ? ag.companyName : ag.customerName}
                </Table.Cell>
                <Table.Cell
                  data-label={`${t("labels.invoice_date", {
                    ns: "inventory",
                  })}:`}
                >
                  {ag.invoiceDate ? format(new Date(ag.invoiceDate), 'P') : null}
                </Table.Cell>
                <Table.Cell
                  className="hoveredLink"
                  onClick={() => window.open(`/ticket/view/${ag.ticketId}`, `_`)}
                  data-label={`${t("ticketnum", { ns: "common" })}:`}
                >
                  Invoice #{ag.invoiceNumber}
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  data-label={`${t("daysOutstanding", {
                    ns: "accountsReceivable",
                  })}:`}
                >
                  {ag.daysOutstanding}
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  data-label={`${t("payReceived", {
                    ns: "accountsReceivable",
                  })}:`}
                >
                  {formatCurrency(ag.paymentsReceived ?? 0.0)}
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  data-label={`1 - 30 ${t("daysOutstanding", {
                    ns: "accountsReceivable",
                  })}:`}
                >
                  {ag.oneToThirtyPastBalanceDue ? formatCurrency(ag.oneToThirtyPastBalanceDue  ?? 0) : null}
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  data-label={`31 - 60 ${t("daysOutstanding", {
                    ns: "accountsReceivable",
                  })}:`}
                >
                  {ag.thirtyToSixtyPastBalanceDue ? formatCurrency(ag.thirtyToSixtyPastBalanceDue  ?? 0) : null}
                </Table.Cell>
                <Table.Cell
                  data-label={`61 - 90 ${t("daysOutstanding", {
                    ns: "accountsReceivable",
                  })}:`}
                  textAlign="right"
                >
                  {ag.sixtyToNinetyPastBalanceDue ? formatCurrency(ag.sixtyToNinetyPastBalanceDue  ?? 0) : null}
                </Table.Cell>
                <Table.Cell
                  data-label={`91+ ${t("daysOutstanding", {
                    ns: "accountsReceivable",
                  })}:`}
                  textAlign="right"
                >
                  {ag.ninetyPlusPastBalanceDue ? formatCurrency(ag.ninetyPlusPastBalanceDue  ?? 0) : null}
                </Table.Cell>
                <Table.Cell
                  data-label={`${t("balanceDue", {
                    ns: "accountsReceivable",
                  })}:`}
                  textAlign="right"
                >
                  {formatCurrency(ag.balanceDue  ?? 0)}
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row className="noBackground">
              <Table.Cell colSpan={16}></Table.Cell>
            </Table.Row>
            <Table.Row className="terms_bold greenRow">
              <Table.Cell>
                {t("totals", { ns: "reports" })}
              </Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell textAlign="right"></Table.Cell>

              <Table.Cell textAlign="right">
                {formatCurrency(
                  calculateOneToThirtyBalanceDue(getAccountsReceivableAgingReport)
                )}
              </Table.Cell>
              <Table.Cell textAlign="right">
                {formatCurrency(
                  calculateThirtyToSixtyBalanceDue(getAccountsReceivableAgingReport)
                )}
              </Table.Cell>
              <Table.Cell textAlign="right">
                {formatCurrency(
                  calculateSixtyToNinetyBalanceDue(getAccountsReceivableAgingReport)
                )}
              </Table.Cell>
              <Table.Cell textAlign="right">
                {formatCurrency(
                  calculateNinetyPlusBalanceDue(getAccountsReceivableAgingReport)
                )}
              </Table.Cell>
              <Table.Cell
                textAlign="right"
                data-label={`${t("labels.totalQuantity", {
                  ns: "inventory",
                })}:`}
              >
                {formatCurrency(
                  calculateTotalBalanceDue(getAccountsReceivableAgingReport)
                )}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Container>
    </>
  );
});
